import { api } from "./api";
import type { DisconnectReason } from "../components/marketplace/DisconnectMarketplaceDialog";
import {
  parseNoContactExit,
  type BuyerConnectionDTO,
  type CheckinCardDTO,
  type ConnectionRequestDTO,
} from "./marketplace-status";
import type { PinnedOffer } from "./threads-api";
import type { MarketplaceRole } from "./marketplace-route";

export interface BuyerMarketplaceConnection {
  id: string;
  role: string;
  status: string;
  pro_name?: string;
  /** Reveal-gated (active only) — absent pre-reveal, exactly like pro_name. */
  pro_user_id?: string;
}

export async function getMarketplaceConnection(
  getToken: () => Promise<string | null>,
  role: MarketplaceRole = "agent",
): Promise<BuyerMarketplaceConnection | null> {
  const token = await getToken();
  const result = await api.get<{
    connection: BuyerMarketplaceConnection | null;
  }>(
    `/api/marketplace/connection?role=${encodeURIComponent(role)}`,
    token ?? undefined,
  );
  return result.connection;
}

/**
 * The buyer's own pending standing-offer connection request for a role →
 * the `pending_request` field of `GET /api/marketplace/connection?role=`
 * (plan 435 M0, additive alongside `connection`). `null` when the buyer
 * has no pending_pro request for that role.
 */
export async function getPendingRequest(
  getToken: () => Promise<string | null>,
  role: MarketplaceRole = "agent",
): Promise<ConnectionRequestDTO | null> {
  const { pendingRequest } = await fetchConnectionState(getToken, role);
  return pendingRequest;
}

/**
 * The combined `connection` + `pending_request` read, ONE `api.get` call
 * (plan 435 M1, mfh-home-settle carryover) — `getConnection` and
 * `getPendingRequest` both proxy to this so no caller needs two round trips
 * to the same endpoint. `useMarketplaceConnection` calls this directly so a
 * single hook mount issues a single request.
 */
export interface ConnectionAndPendingRequest {
  connection: BuyerConnectionDTO | null;
  pendingRequest: ConnectionRequestDTO | null;
}

export async function fetchConnectionState(
  getToken: () => Promise<string | null>,
  role: string,
): Promise<ConnectionAndPendingRequest> {
  const token = await getToken();
  const res = await api.get<{
    connection: WireBuyerConnection | null;
    pending_request?: ConnectionRequestDTO | null;
  }>(`/api/marketplace/connection?role=${encodeURIComponent(role)}`, token ?? undefined);
  return {
    connection: res.connection ? normalizeConnection(res.connection) : null,
    pendingRequest: res.pending_request ?? null,
  };
}

/**
 * The buyer's own withdraw of a pending standing-offer connection request →
 * `POST /api/marketplace/requests/:id/withdraw` (plan 435 M0). Transitions
 * `pending_pro` to `withdrawn_by_buyer` without unpublishing the buyer's
 * passport. Throws on a non-2xx response (a 409 on a non-pending row, a
 * 404 on an unknown/unowned id) — callers render the existing "Couldn't
 * withdraw — please try again." failure state on rejection.
 */
export async function withdrawConnectionRequest(
  getToken: () => Promise<string | null>,
  id: string,
): Promise<void> {
  const token = await getToken();
  await api.post<void>(
    `/api/marketplace/requests/${encodeURIComponent(id)}/withdraw`,
    {},
    token ?? undefined,
  );
}

/**
 * `POST /api/marketplace/connections/:id/disconnect` response (plan 462 M5).
 * Both booleans are always present on the wire; anything else parses to
 * `false` so a malformed or empty body never reads as a republish.
 *
 *  - `republished`: the passport was published again in the same step (only
 *    ever true when the request asked for it and the publish succeeded).
 *  - `no_contact_exit`: the server classified this disconnect as the
 *    "never heard from the agent" exit.
 */
export interface DisconnectResponse {
  republished: boolean;
  no_contact_exit: boolean;
}

export function parseDisconnectResponse(raw: unknown): DisconnectResponse {
  const body =
    typeof raw === "object" && raw !== null
      ? (raw as { republished?: unknown; no_contact_exit?: unknown })
      : {};
  return {
    republished: body.republished === true,
    no_contact_exit: body.no_contact_exit === true,
  };
}

/**
 * `republish` is sent ONLY when true: the dialog offers it solely from the
 * no-contact banner, and every other caller's body stays byte-identical.
 */
export async function disconnectMarketplaceConnection(
  getToken: () => Promise<string | null>,
  id: string,
  reason: DisconnectReason,
  note?: string,
  republish?: boolean,
): Promise<DisconnectResponse> {
  const token = await getToken();
  const body: { reason: DisconnectReason; note?: string; republish?: true } = {
    reason,
    note,
  };
  if (republish === true) {
    body.republish = true;
  }
  const res = await api.post<unknown>(
    `/api/marketplace/connections/${encodeURIComponent(id)}/disconnect`,
    body,
    token ?? undefined,
  );
  return parseDisconnectResponse(res);
}

/**
 * Live buyer-side marketplace connection API (M6-C → M8 wiring). Field
 * names/casing mirror the Go JSON tags in
 * `backend/internal/api/marketplace_connections.go` (`buyerConnectionDTO`
 * — the buyer-facing anonymity chokepoint: no payment vocabulary, pro
 * identity revealed only at `status === "active"`).
 *
 * `escape_eligible` is not yet emitted by the backend `buyerConnectionDTO`
 * (a known M6-C→M8 contract gap — it only gates the day-3 escape button on a
 * held/charging connection). We default it to `false` here so the FE
 * `BuyerConnectionDTO` shape is satisfied without fabricating an escape.
 */

interface WireBuyerConnection {
  id: string;
  role: string;
  status: BuyerConnectionDTO["status"];
  escape_eligible?: boolean;
  pro_name?: string;
  /** Reveal-gated (active only) — absent pre-reveal, exactly like pro_name. */
  pro_user_id?: string;
  pro_contact?: string;
  accepted_offer?: PinnedOffer;
  checkin?: CheckinCardDTO;
  relationship_stage?: string;
  dormant?: boolean;
  /** Plan 462: parsed defensively — never trusted as a typed object. */
  no_contact_exit?: unknown;
}

function normalizeConnection(raw: WireBuyerConnection): BuyerConnectionDTO {
  return {
    id: raw.id,
    role: raw.role,
    status: raw.status,
    // Only real default: the backend doesn't emit escape_eligible yet.
    escape_eligible: raw.escape_eligible ?? false,
    pro_name: raw.pro_name,
    pro_user_id: raw.pro_user_id,
    pro_contact: raw.pro_contact,
    // Frozen accepted offer (M8-A). Must be carried through here — this
    // field-by-field normalizer silently dropped it before, so the connected
    // card never received the offer despite the backend emitting it.
    accepted_offer: raw.accepted_offer,
    checkin: raw.checkin,
    // Plan 435 M4 (mfh-connected-stack): the backend has emitted both since
    // plan 291/M8-B — this normalizer simply never carried them through, so
    // the connected card's RelationshipStageChip had nothing to read.
    relationship_stage: raw.relationship_stage,
    dormant: raw.dormant,
    // Plan 462 M5: the server owns eligibility; a missing or malformed block
    // is "not eligible", never an inferred one.
    no_contact_exit: parseNoContactExit(raw.no_contact_exit),
  };
}

/**
 * Accept one of the buyer's own active offers →
 * `POST /api/marketplace/offers/:id/accept` (no body — pro/role/tier derived
 * server-side from the offer). Opens a `charging` connection; the
 * charge→reveal payoff runs async via Temporal (buyer polls `getConnection`).
 */
export async function acceptActiveOffer(
  getToken: () => Promise<string | null>,
  offerId: string,
): Promise<{ connection: BuyerConnectionDTO }> {
  const token = await getToken();
  const res = await api.post<{ connection: WireBuyerConnection }>(
    `/api/marketplace/offers/${encodeURIComponent(offerId)}/accept`,
    {},
    token ?? undefined,
  );
  return { connection: normalizeConnection(res.connection) };
}

/**
 * What a standing accept answers with. A stranger's accept opens a 72h
 * `pending_pro` request (`{request}`); the buyer's OWN client (an active
 * link to the pro) is bound instantly as a $0 retention connection, already
 * `active`, and gets `{connection}` instead. The server decides which from
 * the live link — callers branch on the key that is present.
 */
export type StandingAcceptResult =
  | { request: ConnectionRequestDTO; connection?: undefined }
  | { connection: BuyerConnectionDTO; request?: undefined };

export async function acceptStandingOffer(
  getToken: () => Promise<string | null>,
  proId: string,
  buyerTier: string,
): Promise<StandingAcceptResult> {
  const token = await getToken();
  const res = await api.post<{
    request?: ConnectionRequestDTO;
    connection?: WireBuyerConnection;
  }>(
    `/api/agents/${encodeURIComponent(proId)}/standing/accept`,
    { role: "agent", buyer_tier: buyerTier },
    token ?? undefined,
  );
  if (res.connection) return { connection: normalizeConnection(res.connection) };
  if (res.request) return { request: res.request };
  throw new Error("standing accept answered with neither a request nor a connection");
}

/**
 * Buyer's 72h escape hatch on a held/charging connection →
 * `POST /api/marketplace/connections/:id/cancel`. Server enforces the escape
 * window (age ≥ 72h AND not yet charged); a too-early call 409s (surfaced to
 * the caller as a rejected promise — the status card shows a calm soft note).
 */
export async function cancelConnection(
  getToken: () => Promise<string | null>,
  connectionId: string,
): Promise<void> {
  const token = await getToken();
  await api.post<void>(
    `/api/marketplace/connections/${encodeURIComponent(connectionId)}/cancel`,
    {},
    token ?? undefined,
  );
}

/**
 * Buyer's own view of their connection for a role →
 * `GET /api/marketplace/connection?role=`. Returns `null` when the buyer has
 * no connection for that role yet (a normal state, 200 not 404).
 */
export async function getConnection(
  getToken: () => Promise<string | null>,
  role: string,
): Promise<BuyerConnectionDTO | null> {
  const { connection } = await fetchConnectionState(getToken, role);
  return connection;
}

/** The buyer's OWN review of one pro, as rendered back to them. Reviews are
 *  never edited (operator 2026-10-09), so there is no edit marker. */
export interface MyReview {
  id: string;
  pro_user_id: string;
  stars: number;
  body: string;
  label: "worked_together" | "closed";
  origin: "marketplace" | "invited";
  incentive_honored?: ReviewIncentiveHonored;
  created_at: string;
  updated_at: string;
  /** Invited-origin reviews only: when the review starts appearing on the
   *  agent's profile (SEC-15). Omitted for marketplace-origin reviews. */
  counts_after?: string | null;
}

/** The one review ask the server says a surface may offer for a pair. */
export type ReviewAsk = "worked_together" | "closed";

/**
 * `GET /api/marketplace/reviews?pro_user_id=` — the buyer's own review plus
 * the server's decision of which ask (if any) to offer. Every surface renders
 * from `nextAsk`; none decides on its own (review stage rules, 2026-10-09).
 * `closeConfirmRequired` marks a closed ask whose only evidence is the
 * agent's CRM close: the sheet must collect the buyer's own confirmation.
 */
export interface MyReviewState {
  review: MyReview | null;
  nextAsk: ReviewAsk | null;
  closeConfirmRequired: boolean;
}

export async function getMyReviewState(
  getToken: () => Promise<string | null>,
  proUserId: string,
): Promise<MyReviewState> {
  const token = await getToken();
  const res = await api.get<{
    review: MyReview | null;
    next_ask: ReviewAsk | null;
    close_confirm_required: boolean;
  }>(
    `/api/marketplace/reviews?pro_user_id=${encodeURIComponent(proUserId)}`,
    token ?? undefined,
  );
  return {
    review: res.review ?? null,
    nextAsk: res.next_ask ?? null,
    closeConfirmRequired: res.close_confirm_required === true,
  };
}

/**
 * Buyer's post-connection review submission →
 * `POST /api/marketplace/reviews`. Deliberately omits any `label` field —
 * mirrors the backend `submitReviewBody`/`reviews.Service.Submit` contract,
 * which derives `worked_together` itself; the client must not even attempt
 * to send one. Creates only: a pair that already has a review gets 409
 * `review_exists`.
 */
export async function submitMarketplaceReview(
  getToken: () => Promise<string | null>,
  proUserId: string,
  stars: number,
  body: string,
): Promise<{ id: string }> {
  const token = await getToken();
  return api.post<{ id: string }>(
    "/api/marketplace/reviews",
    { pro_user_id: proUserId, stars, body },
    token ?? undefined,
  );
}

/** The buyer's post-close upgrade of an incentive-question value (D2). */
export type ReviewIncentiveHonored = "yes" | "partial" | "no";

/**
 * Buyer's post-connection review upgrade (worked_together → closed) →
 * `PUT /api/marketplace/reviews/:id`. Mirrors `submitMarketplaceReview`'s
 * optional-field contract exactly: `incentiveHonored` is omitted from the
 * wire body (never sent as `undefined`) when the caller doesn't pass it,
 * matching the backend `upgradeReviewBody.IncentiveHonored *string` pointer
 * semantics (`backend/internal/api/marketplace_reviews.go`).
 *
 * `confirmClosed` is the buyer's in-sheet "we closed" confirmation, sent only
 * when the server asked for it (`closeConfirmRequired`). The closed stage is
 * answered once: a second upgrade gets 409 `review_exists`, and an upgrade
 * with no buyer close gets 409 `close_not_confirmed`.
 */
export async function upgradeMarketplaceReview(
  getToken: () => Promise<string | null>,
  reviewId: string,
  stars: number,
  body: string,
  incentiveHonored?: ReviewIncentiveHonored,
  confirmClosed?: boolean,
): Promise<{ id: string }> {
  const token = await getToken();
  const wireBody: {
    stars: number;
    body: string;
    incentive_honored?: ReviewIncentiveHonored;
    confirm_closed?: true;
  } = { stars, body };
  if (incentiveHonored !== undefined) {
    wireBody.incentive_honored = incentiveHonored;
  }
  if (confirmClosed) {
    wireBody.confirm_closed = true;
  }
  return api.put<{ id: string }>(
    `/api/marketplace/reviews/${encodeURIComponent(reviewId)}`,
    wireBody,
    token ?? undefined,
  );
}

/** The buyer's one-tap check-in card answer (spec §3, D11). */
export type CheckinResponse = "closed" | "still_looking" | "parted";

export interface CheckinAnswerResult {
  ok: true;
  /** Present ONLY for a `parted` answer — the deep-link affordance hint the
   *  CheckinCard uses to open the existing disconnect flow. This endpoint
   *  NEVER auto-disconnects. */
  next?: "disconnect";
}

/**
 * Buyer's one-tap check-in card answer →
 * `POST /api/marketplace/connection/checkin?role=`. `role` matches the
 * connection's own role (mirrors `getConnection`'s role-scoped read — the
 * backend re-resolves ownership from role + the caller's session, never
 * from a connection id in the body).
 */
export async function postConnectionCheckin(
  getToken: () => Promise<string | null>,
  role: string,
  response: CheckinResponse,
): Promise<CheckinAnswerResult> {
  const token = await getToken();
  return api.post<CheckinAnswerResult>(
    `/api/marketplace/connection/checkin?role=${encodeURIComponent(role)}`,
    { response },
    token ?? undefined,
  );
}
